import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  BarChart3,
  DollarSign,
  Settings,
  X,
  Smartphone,
  UserCog
} from 'lucide-react';

const Sidebar = ({ isOpen, isMobile, onClose }) => {
  const location = useLocation();

  const navigationItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: LayoutDashboard,
      description: 'Overview and stats'
    },
    {
      name: 'Users',
      path: '/users',
      icon: Users,
      description: 'Manage user accounts'
    },
    {
      name: 'Transactions',
      path: '/transactions',
      icon: CreditCard,
      description: 'View all transactions'
    },
    {
      name: 'Analytics',
      path: '/analytics',
      icon: BarChart3,
      description: 'Reports and insights'
    },
    {
      name: 'Commission',
      path: '/commission',
      icon: DollarSign,
      description: 'Commission earnings'
    }
  ];

  const secondaryItems = [
    {
      name: 'Admin Management',
      path: '/admin-management',
      icon: UserCog,
      description: 'Manage admin users'
    },
    {
      name: 'Settings',
      path: '/settings',
      icon: Settings,
      description: 'General settings'
    }
  ];

  const isActiveRoute = (path) => {
    if (path === '/dashboard' && location.pathname === '/') return true;
    return location.pathname === path;
  };

  const NavItem = ({ item, isActive, onClick }) => {
    const Icon = item.icon;
    
    return (
      <NavLink
        to={item.path}
        onClick={onClick}
        className={`
          flex items-center px-6 py-3 text-sm font-medium transition-all duration-200
          ${isActive 
            ? 'nav-item-active bg-vetme-mint-very-pale text-vetme-dark border-r-4 border-vetme-mint' 
            : 'nav-item-inactive text-vetme-gray-dark hover:bg-vetme-gray-lightest hover:text-vetme-dark'
          }
        `}
      >
        <Icon className={`w-5 h-5 mr-3 ${isActive ? 'text-vetme-mint-dark' : 'text-vetme-gray-dark'}`} />
        <div className="flex-1">
          <div className="font-medium">{item.name}</div>
          <div className={`text-xs ${isActive ? 'text-vetme-mint-dark' : 'text-vetme-gray-dark'}`}>
            {item.description}
          </div>
        </div>
      </NavLink>
    );
  };

  const SectionHeader = ({ title }) => (
    <div className="px-6 py-3">
      <h3 className="text-xs font-semibold text-vetme-gray-dark uppercase tracking-wider">
        {title}
      </h3>
    </div>
  );

  return (
    <>
      {/* Sidebar */}
      <div className={`
        ${isMobile 
          ? `fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out ${
              isOpen ? 'translate-x-0' : '-translate-x-full'
            }`
          : 'relative w-72'
        }
        bg-white border-r border-vetme-gray-light flex flex-col
      `}>
        {/* Sidebar header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-vetme-gray-light">
          <div className="flex items-center space-x-3">
            {/* VetMe Logo */}
            <div className="w-8 h-8 bg-vetme-dark rounded-full flex items-center justify-center">
              <div className="w-4 h-4 relative">
                <div className="absolute inset-0 bg-white rounded-full opacity-20"></div>
                <div className="absolute top-1 left-1 w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
            <div>
              <span className="vetme-logo text-lg text-vetme-dark font-light">vetme</span>
              <div className="text-xs text-vetme-gray-dark">Admin Dashboard</div>
            </div>
          </div>
          
          {/* Close button for mobile */}
          {isMobile && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-vetme-gray-lightest focus:outline-none focus:ring-2 focus:ring-vetme-mint"
            >
              <X className="w-5 h-5 text-vetme-gray-dark" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {/* Main navigation */}
          <div className="space-y-1">
            {navigationItems.map((item) => (
              <NavItem
                key={item.path}
                item={item}
                isActive={isActiveRoute(item.path)}
                onClick={isMobile ? onClose : undefined}
              />
            ))}
          </div>

          {/* Secondary navigation */}
          <div className="mt-8">
            <SectionHeader title="Configuration" />
            <div className="space-y-1">
              {secondaryItems.map((item) => (
                <NavItem
                  key={item.path}
                  item={item}
                  isActive={isActiveRoute(item.path)}
                  onClick={isMobile ? onClose : undefined}
                />
              ))}
            </div>
          </div>
        </nav>

        {/* Sidebar footer */}
        <div className="px-6 py-4 border-t border-vetme-gray-light">
          <div className="card-vetme p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-vetme-mint-very-pale rounded-lg flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-vetme-mint-dark" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-vetme-dark">WhatsApp Status</div>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-xs text-vetme-gray-dark">Connected</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;